import React, { useState, createContext, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../../../Components/Common/Breadcrumb";
import DomainSearchStep from "./DomainSearch";
import DomainPricing from "./DomainPricing";
import { DomainFlowState, DomainFlowStep, DomainFlowContextType } from "./types";

// Create context for flow state management
export const DomainFlowContext = createContext<DomainFlowContextType | null>(null);

// Hook to use domain flow context
export const useDomainFlow = () => {
  const context = useContext(DomainFlowContext);
  if (!context) {
    throw new Error("useDomainFlow must be used within DomainResellerFlow");
  }
  return context;
};

const DomainResellerFlow: React.FC = () => {
  document.title = "Domain Reselling | BizServe";

  const [state, setState] = useState<DomainFlowState>({
    searchResults: [],
    selectedDomain: null,
    domainPricing: null,
    registrationData: null,
    contacts: null,
    nameservers: null,
    options: {
      whoisPrivacy: false,
      autoRenew: true,
    },
    currentStep: "search",
    isLoading: false,
    error: null,
  });

  const [alertMsgg, setAlertMsgg] = useState<any>({
    open: false,
    type: "success",
    content: "",
  });

  const handleAlertMsggClose = () => {
    setAlertMsgg({ ...alertMsgg, open: false });
  };

  const updateState = (updates: Partial<DomainFlowState>) => {
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  const goToStep = (step: DomainFlowStep) => {
    setState((prevState) => ({
      ...prevState,
      currentStep: step,
      error: null,
    }));
  };

  const nextStep = () => {
    const steps: DomainFlowStep[] = [
      "search",
      "pricing",
      "register",
      "contacts",
      "dns",
      "options",
      "confirmation",
    ];
    const currentIndex = steps.indexOf(state.currentStep);
    if (currentIndex < steps.length - 1) {
      goToStep(steps[currentIndex + 1]);
    }
  };

  const previousStep = () => {
    const steps: DomainFlowStep[] = [
      "search",
      "pricing",
      "register",
      "contacts",
      "dns",
      "options",
      "confirmation",
    ];
    const currentIndex = steps.indexOf(state.currentStep);
    if (currentIndex > 0) {
      goToStep(steps[currentIndex - 1]);
    }
  };

  const resetFlow = () => {
    setState({
      searchResults: [],
      selectedDomain: null,
      domainPricing: null,
      registrationData: null,
      contacts: null,
      nameservers: null,
      options: {
        whoisPrivacy: false,
        autoRenew: true,
      },
      currentStep: "search",
      isLoading: false,
      error: null,
    });
  };

  const contextValue: DomainFlowContextType = {
    state,
    updateState,
    goToStep,
    nextStep,
    previousStep,
    resetFlow,
  };

  const steps: { key: DomainFlowStep; label: string; description: string }[] = [
    { key: "search", label: "Search", description: "Find available domains" },
    { key: "pricing", label: "Pricing", description: "View pricing details" },
    { key: "register", label: "Register", description: "Purchase domain" },
    { key: "contacts", label: "Contacts", description: "Set domain contacts" },
    { key: "dns", label: "DNS", description: "Configure nameservers" },
    { key: "options", label: "Options", description: "Privacy & auto-renew" },
    { key: "confirmation", label: "Complete", description: "Registration complete" },
  ];

  const getStepIndex = (step: DomainFlowStep): number => {
    return steps.findIndex((s) => s.key === step);
  };

  // Validate if step data is actually complete
  const isStepDataComplete = (step: DomainFlowStep): boolean => {
    switch (step) {
      case "search":
        // Search step is complete if a domain has been selected
        return state.selectedDomain !== null;
      
      case "pricing":
        // Pricing step is complete if pricing data exists or domain is selected (pricing might be shown inline)
        return state.domainPricing !== null || state.selectedDomain !== null;
      
      case "register":
        // Register step is complete if registration data exists
        return state.registrationData !== null;
      
      case "contacts":
        // Contacts step is complete if contacts data exists
        return state.contacts !== null;
      
      case "dns":
        // DNS step is complete if nameservers are set (can be empty array for default)
        return state.nameservers !== null;
      
      case "options":
        // Options step is complete if options object exists (always exists, but check if it's been configured)
        return state.options !== null;
      
      case "confirmation":
        // Confirmation step is complete if all previous steps are complete
        return (
          isStepDataComplete("search") &&
          isStepDataComplete("pricing") &&
          isStepDataComplete("register") &&
          isStepDataComplete("contacts") &&
          isStepDataComplete("dns") &&
          isStepDataComplete("options")
        );
      
      default:
        return false;
    }
  };

  // Check if step is actually completed (has data) or just passed through
  const isStepCompleted = (step: DomainFlowStep): boolean => {
    // A step is completed if its data is complete
    return isStepDataComplete(step);
  };

  // Check if step is currently active
  const isStepActive = (step: DomainFlowStep): boolean => {
    return state.currentStep === step;
  };

  // Check if step is accessible (can be navigated to)
  const isStepAccessible = (step: DomainFlowStep): boolean => {
    const stepIndex = getStepIndex(step);
    const currentIndex = getStepIndex(state.currentStep);
    
    // Can access current step
    if (stepIndex === currentIndex) return true;
    
    // Can access completed steps (go back)
    if (stepIndex < currentIndex && isStepCompleted(step)) return true;
    
    // Can access next step if current step is completed
    if (stepIndex === currentIndex + 1 && isStepCompleted(state.currentStep)) return true;
    
    // Can access any step if all previous steps are completed
    if (stepIndex > currentIndex) {
      // Check if all steps before the target step are completed
      for (let i = 0; i < stepIndex; i++) {
        if (!isStepDataComplete(steps[i].key)) {
          return false;
        }
      }
      return true;
    }
    
    return false;
  };

  // Get step status for better tracking
  const getStepStatus = (step: DomainFlowStep): 'completed' | 'active' | 'pending' | 'incomplete' => {
    if (isStepActive(step)) return 'active';
    if (isStepCompleted(step)) return 'completed';
    const stepIndex = getStepIndex(step);
    const currentIndex = getStepIndex(state.currentStep);
    if (stepIndex < currentIndex && !isStepCompleted(step)) return 'incomplete';
    return 'pending';
  };

  const renderStepContent = () => {
    switch (state.currentStep) {
      case "search":
        return <DomainSearchStep />;
      case "pricing":
        return <DomainPricing />;
      case "register":
        return (
          <Card className="rounded-4 border">
            <CardBody>
              <h4 className="card-title mb-4">Register Domain</h4>
              <p>Registration step - Coming soon</p>
            </CardBody>
          </Card>
        );
      case "contacts":
        return (
          <Card className="rounded-4 border">
            <CardBody>
              <h4 className="card-title mb-4">Domain Contacts</h4>
              <p>Contacts step - Coming soon</p>
            </CardBody>
          </Card>
        );
      case "dns":
        return (
          <Card className="rounded-4 border">
            <CardBody>
              <h4 className="card-title mb-4">DNS Configuration</h4>
              <p>DNS step - Coming soon</p>
            </CardBody>
          </Card>
        );
      case "options":
        return (
          <Card className="rounded-4 border">
            <CardBody>
              <h4 className="card-title mb-4">Additional Options</h4>
              <p>Options step - Coming soon</p>
            </CardBody>
          </Card>
        );
      case "confirmation":
        return (
          <Card className="rounded-4 border">
            <CardBody>
              <h4 className="card-title mb-4">Registration Complete</h4>
              <p>Confirmation step - Coming soon</p>
            </CardBody>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <DomainFlowContext.Provider value={contextValue}>
      <div className="page-content" style={{ paddingTop: "1rem" }}>
        <Container fluid>
          <Breadcrumbs title="Domain Reselling" breadcrumbItem="Domain Registration" />

          {alertMsgg.open && (
            <Alert
              color={
                alertMsgg.type === "success"
                  ? "success"
                  : alertMsgg.type === "info"
                  ? "info"
                  : "danger"
              }
              toggle={handleAlertMsggClose}
              fade={false}
              style={{ fontWeight: "bold" }}
            >
              {alertMsgg.content}
            </Alert>
          )}

          {state.error && (
            <Alert color="danger" fade={false} style={{ fontWeight: "bold" }}>
              {state.error}
            </Alert>
          )}

          {/* Step Navigation */}
          <Row className="mb-2">
            <Col md={12}>
              <Card className="rounded-4 border mb-2">
                <CardBody style={{ padding: "0.75rem" }}>
                  <Nav 
                    pills 
                    className="nav-pills-custom"
                    style={{
                      display: "flex",
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      gap: "0.25rem",
                    }}
                  >
                    {steps.map((step, index) => (
                      <NavItem 
                        key={step.key} 
                        style={{
                          flex: "1 1 0",
                          minWidth: "0",
                        }}
                      >
                        <NavLink
                          className={`${
                            isStepActive(step.key) ? "active" : ""
                          } ${
                            isStepCompleted(step.key) ? "completed" : ""
                          }`}
                          onClick={() => {
                            // Only allow navigation to accessible steps
                            if (isStepAccessible(step.key)) {
                              goToStep(step.key);
                            }
                          }}
                          style={{
                            cursor: isStepAccessible(step.key) ? "pointer" : "not-allowed",
                            opacity: isStepAccessible(step.key) ? 1 : 0.5,
                            padding: "0.5rem 0.25rem",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "75px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "1.75rem",
                              fontWeight: "bold",
                              lineHeight: "1",
                              marginBottom: "0.25rem",
                              color: (() => {
                                if (isStepActive(step.key)) return "#ffffff";
                                if (isStepCompleted(step.key)) return "#28a745";
                                // Check if step was passed through but not completed
                                const stepIndex = getStepIndex(step.key);
                                const currentIndex = getStepIndex(state.currentStep);
                                if (stepIndex < currentIndex && !isStepCompleted(step.key)) {
                                  return "#ffc107"; // Warning color for incomplete steps
                                }
                                return "#6c757d"; // Default gray for pending steps
                              })(),
                            }}
                          >
                            {isStepCompleted(step.key) ? "✓" : index + 1}
                          </div>
                          <div className="d-flex flex-column align-items-center">
                            <span 
                              className="fw-bold"
                              style={{
                                fontSize: "0.75rem",
                                marginBottom: "0.1rem",
                                color: isStepActive(step.key) ? "#ffffff" : "inherit",
                              }}
                            >
                              {step.label}
                            </span>
                            <small 
                              style={{
                                fontSize: "0.65rem",
                                textAlign: "center",
                                color: isStepActive(step.key) ? "rgba(255, 255, 255, 0.8)" : "#6c757d",
                              }}
                            >
                              {step.description}
                            </small>
                          </div>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Step Content */}
          <Row>
            <Col md={12}>{renderStepContent()}</Col>
          </Row>

          {/* Navigation Buttons */}
          {state.currentStep !== "search" && state.currentStep !== "confirmation" && (
            <Row className="mt-4">
              <Col md={12} className="d-flex justify-content-between">
                <Button
                  color="secondary"
                  onClick={previousStep}
                  disabled={state.isLoading}
                >
                  Previous
                </Button>
                <Button
                  color="primary"
                  onClick={nextStep}
                  disabled={state.isLoading || !state.selectedDomain}
                >
                  {state.isLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    "Next"
                  )}
                </Button>
              </Col>
            </Row>
          )}

          {state.currentStep === "confirmation" && (
            <Row className="mt-4">
              <Col md={12} className="d-flex justify-content-center">
                <Button color="primary" onClick={resetFlow}>
                  Register Another Domain
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </DomainFlowContext.Provider>
  );
};

export default DomainResellerFlow;
